import { ROUTE_FORBIDDEN, ROUTE_UNAUTHORIZED } from '@/router/routes/routeNames'

/**
 *  Checks if the current user has permission to access the content on Mainpage
 * @param {Object} currentCompany Current company
 * @param {Object} currentUser Current user
 * @param {Object} route Route to be tested for access
 * @param {Object} ability Plugin to check right access
 * @returns {Boolean} If true then access is granted, otherwise it is not
 */
export const checkAccessForNavigation = (
  currentCompany,
  currentUser,
  route,
  ability
) => {
  const isInitial = route.path === '/'

  const isUnauthorized = route.name === ROUTE_UNAUTHORIZED
  const isNotAllowed = route.name === ROUTE_FORBIDDEN

  if (isUnauthorized || isNotAllowed) {
    return true
  }

  if (isInitial) {
    return true
  }

  // The accessible is one half of the gate; the flag check below is the other
  const abacResource = route.meta.abacResource
  if (abacResource && !hasAccessToAbacResource(currentUser, abacResource)) {
    return false
  }

  if (!hasRequiredFeatureFlags(currentUser, currentCompany, route.meta)) {
    return false
  }

  if (
    route.name === 'ROUTE_USER_APPROVALS' &&
    isApprovalsDisabled(currentUser, currentCompany)
  ) {
    return false
  }

  const isAllowed = route.matched.every((route) => {
    let canRead = false
    const routeRequiresPortal = route.meta.requiresPortal || false
    const routeRequiresNews = route.meta.requiresNews || false
    const routeRequiresCompany = route.meta.resource === 'Company'
    const hasPortal = currentCompany.supported_configurations.includes('portal')
    const hasNews =
      currentCompany.portal_configurations.includes('enable_portal_news')
    const hasCompanyRelationships = currentCompany.feature_flags.includes(
      'enable_company_relationships'
    )

    if (routeRequiresPortal && !hasPortal) {
      return false
    }
    if (routeRequiresNews && !hasNews) {
      return false
    }
    if (routeRequiresCompany && !hasCompanyRelationships) {
      return false
    }
    if (route.path === '') {
      return true
    }
    if (Array.isArray(route.meta.resource)) {
      canRead = route.meta.resource.reduce((res, a) => {
        return res && ability.can('read', a)
      }, true)
    } else {
      canRead = ability.can('read', route.meta.resource)
    }

    return canGo(currentCompany, route, canRead)
  })

  return isAllowed
}

/**
 *  Checks if the current user has permission to access the content on Sidebar and SidebarItem
 * @param {Object} currentCompany Current company
 * @param {Object} currentUser Current user
 * @param {Object} router Router Plugin which holds the routes to be tested for access
 * @param {Object} ability Plugin to check right access
 * @param {Object} item item of the current item of the sidebar
 * @returns {Boolean} If true then access is granted, otherwise it is not
 */
export const checkAccessForSidebar = (
  currentCompany,
  currentUser,
  router,
  ability,
  item
) => {
  const route = router.options.routes[0].children.find((r) => {
    // Match on name instead of link as all routes are named and matched in the sidebar items list
    // This way we don't have to worry about the link being dynamic
    return r.name === item.name
  })

  if (typeof route === 'undefined') {
    return true
  }

  // The accessible is one half of the gate; the flag check below is the other
  const abacResource = route.meta.abacResource
  if (abacResource && !hasAccessToAbacResource(currentUser, abacResource)) {
    return false
  }

  if (!hasRequiredFeatureFlags(currentUser, currentCompany, route.meta)) {
    return false
  }

  if (
    route.name === 'ROUTE_USER_APPROVALS' &&
    isApprovalsDisabled(currentUser, currentCompany)
  ) {
    return false
  }

  const routeRequiresPortal = route.meta.requiresPortal || false
  const routeRequiresNews = route.meta.requiresNews || false
  const routeRequiresCompany = route.meta.resource === 'Company'
  const hasPortal = currentCompany.supported_configurations.includes('portal')
  const hasNews =
    currentCompany.portal_configurations.includes('enable_portal_news')
  const hasCompanyRelationships = currentCompany.feature_flags.includes(
    'enable_company_relationships'
  )

  if (routeRequiresPortal && !hasPortal) {
    return false
  }

  if (routeRequiresNews && !hasNews) {
    return false
  }

  if (routeRequiresCompany && !hasCompanyRelationships) {
    return false
  }

  const canRead = ability.can('read', item.subject)

  return canGo(currentCompany, route, canRead)
}

/**
 *  Checks if the current user has permission to access the content
 * @param {Object} currentCompany Current company
 * @param {Object} route Route to be tested for access
 * @param {Boolean} canRead Access from role
 * @returns {Boolean} If true then access is granted, otherwise it is not
 */
const canGo = (currentCompany, route, canRead) => {
  let routeMaster = route.meta.isMaster

  if (typeof routeMaster === 'undefined' && canRead) {
    return true
  }

  // some paths can only be accessed if company is not the master of data
  routeMaster = false
  const isNotMaster = currentCompany.user_data_master === routeMaster

  return isNotMaster && canRead
}

// Unlike `meta.resource`, read straight from the enabled flags, so a role whose
// ability reads every subject (superadmin) is gated too
const hasRequiredFeatureFlags = (user, company, meta) => {
  const requiredFlags = meta.requiredFeatureFlags
  if (!requiredFlags) return true

  const enabledFlags = [
    ...(user.features?.adminview ?? []),
    ...(company.adminview_configurations ?? []),
  ]
  return requiredFlags.every((flag) => enabledFlags.includes(flag))
}

const hasAccessToAbacResource = (user, resource) => {
  const policies = user.v3.accessibles
  const matchingPolicy = policies.find(
    (policy) => policy.accessible_resource?.name === resource
  )
  return matchingPolicy !== undefined
}

const isManagerOnly = (userRoles) => {
  if (userRoles.includes('superadmin')) {
    return false
  }
  if (userRoles.includes('superuser')) {
    return false
  }
  if (userRoles.includes('manager')) {
    return true
  }
}

const isApprovalsDisabled = (currentUser, currentCompany) => {
  const isManager = isManagerOnly(currentUser.roles)
  const hasApprovalsDisabled = currentCompany.adminview_configurations.includes(
    'disable_approval_page_for_manager'
  )
  return isManager && hasApprovalsDisabled
}
